'use client';

import { useLocalStorageState } from '~/modules/browser/storage';
import { contextFactory } from '~/utils/react';
import { CACHED_PARTICIPANTS_FILTER } from '../search/client/context';
import { parseAsInteger, useQueryState } from 'nuqs';
import { useMemo } from 'react';
import { std } from '@funkey/shared';
import type { ActivityPriceType } from '@funkey/constants';
import type { ActivityFields } from './shared';
import { calculatePrice } from '@funkey/shared/activities';
import { useVATRate } from '~/features/portals/money';

export const [ActivityPDP_useCtx, ActivityPDP_CtxProvider] = contextFactory(
    (activity: {
        id: ActivityFields['id'];
        priceType: ActivityPriceType;
        activityVariants: ActivityFields['activityVariants'];
        activityLanguages: ActivityFields['activityLanguages'];
        minParticipants: number | null;
        maxParticipants: number | null;
    }) => {
        const localStorageState = useLocalStorageState(CACHED_PARTICIPANTS_FILTER);
        const [queryState, setQueryState] = useQueryState(
            'participants',
            parseAsInteger.withOptions({
                scroll: false,
                shallow: true,
                clearOnDefault: true,
            }),
        );

        const participants = useMemo(() => {
            function clamp(participants: number) {
                return std.Number.clamp(
                    participants,
                    activity.minParticipants,
                    activity.maxParticipants,
                );
            }

            return {
                isMounted: localStorageState.isMounted,
                state: clamp(
                    queryState ??
                        // Just get directly from the store because there is a state race condition
                        (localStorageState.isMounted ? localStorageState.store.getValue() : null) ??
                        CACHED_PARTICIPANTS_FILTER.initialValue,
                ),
                setState(participants: number) {
                    const nextParticipants = clamp(participants);
                    setQueryState(nextParticipants);
                    localStorageState.setState(nextParticipants);
                },

                min: activity.minParticipants,
                max: activity.maxParticipants,
                clamp,
            };
        }, [
            queryState,
            localStorageState.state,
            localStorageState.isMounted,
            activity.minParticipants,
            activity.maxParticipants,
        ]);

        const vat = useVATRate();
        const variants = useMemo(() => {
            return activity.activityVariants.map(variant => {
                const prices = calculatePrice({
                    participants: participants.state,
                    priceType: activity.priceType,
                    variant,
                });

                return {
                    ...variant,
                    pricePerParticipant: vat.calcEUR(prices.pricePerParticipant),
                };
            });
        }, [participants.state, activity.activityVariants, vat]);

        return {
            /** Page-wide participants input state */
            participants,

            /** PDP Activity */
            activity: {
                id: activity.id,
                /** Activity variants with `pricePerParticipant` already calculated */
                variants,
                languages: activity.activityLanguages,

                priceType: activity.priceType,
                /** Minimum and maximum variant prices based on the selected number of `participants` */
                priceRange: useMemo(() => {
                    const prices = variants.map(variant => variant.pricePerParticipant);
                    return {
                        min: Math.min(...prices),
                        max: Math.max(...prices),
                    };
                }, [variants]),
            },
        };
    },
);
